import React from 'react';
import { LuPlus, LuTrendingUp, LuArrowRight, LuFlipHorizontal } from 'react-icons/lu';
import EmptyState from '../oracle/EmptyState';
import { EYE_LINE_PCT } from '../oracle/PortraitScoreBar';
import { glowupVectorLabel } from '../../contracts/glowupApi';
import { COPY } from '../../contracts/copyMap';

// Vault GLOWUPS sub-tab. Design-recreation mockup: italic sub + NEW GLOWUP
// gold pill, then a 2-col grid (stacked on mobile) of glowup cards:
//
//   ┌──────────────────────────────────────────────┐
//   │ SPAN LABEL                {vector pill}      │
//   │ 6-Month Glowup            {n} JUDGMENTS      │
//   │ ┌─ BEFORE ─┐  ┌─ AFTER ──┐                   │
//   │ │ ▇▇▇▇▇▇▇ │  │ ▇▇▇▇▇▇▇ │  ← anonymous bar  │
//   │ └──────────┘  └──────────┘                   │
//   │ Jan 2025 → Jul 2025                 VIEW →   │
//   └──────────────────────────────────────────────┘
//
// The design's before/after score bars + "+1.5" delta badge are replaced:
// the glowup contract (OwnGlowupRow) carries no scan scores, so the eye-line
// bar renders in the plain CensorBar ANONYMOUS treatment (solid black, no
// number) and the badge slot shows biggest_vector + responses_seen instead.
//
// Props:
//   rows    OwnGlowupRow[]
//   onNew   () => void   NEW GLOWUP → /create?mode=glowup
//   onView  (glowupId) => void

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function parseDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatMonthYear(iso) {
  const d = parseDate(iso);
  if (!d) return '';
  return `${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

// "JAN – JUL 2025" when both dates share a year, otherwise
// "NOV 2024 – MAY 2025".
function formatSpanLabel(beforeIso, afterIso) {
  const b = parseDate(beforeIso);
  const a = parseDate(afterIso);
  if (!b || !a) return '';
  const bm = MONTHS_SHORT[b.getUTCMonth()].toUpperCase();
  const am = MONTHS_SHORT[a.getUTCMonth()].toUpperCase();
  if (b.getUTCFullYear() === a.getUTCFullYear()) {
    return `${bm} – ${am} ${a.getUTCFullYear()}`;
  }
  return `${bm} ${b.getUTCFullYear()} – ${am} ${a.getUTCFullYear()}`;
}

// Whole-month span computed from the real taken_at dates.
function monthsBetween(beforeIso, afterIso) {
  const b = parseDate(beforeIso);
  const a = parseDate(afterIso);
  if (!b || !a) return 0;
  return Math.max(
    0,
    (a.getUTCFullYear() - b.getUTCFullYear()) * 12 + (a.getUTCMonth() - b.getUTCMonth()),
  );
}

function glowupTitle(row) {
  const months = monthsBetween(row.before_taken_at, row.after_taken_at);
  return months > 0 ? `${months}-Month Glowup` : 'Glowup';
}

const VaultGlowupsView = ({
  rows = [],
  onNew,
  onView,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
}) => {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <EmptyState
          headline={COPY.GLOWUPS_INDEX_EMPTY_HEADLINE}
          subtitle={COPY.GLOWUPS_INDEX_EMPTY_SUBTITLE}
          ctaLabel="NEW GLOWUP"
          onCta={onNew}
          icon={<LuFlipHorizontal size={22} />}
        />
      </div>
    );
  }

  return (
    <div className="mt-5 lg:mt-8">
      <div className="flex justify-between items-center">
        <p className="font-display italic text-bone-dim m-0 text-sm lg:text-lg">
          Your before / after progress, archived privately.
        </p>
        <button
          type="button"
          onClick={onNew}
          className="flex items-center gap-2 lg:gap-2.5 bg-transparent border border-[rgba(201,164,92,0.5)] rounded-full px-3 py-[7px] lg:px-5 lg:py-[11px] cursor-pointer text-aurum-bright font-display uppercase tracking-[0.2em] transition-all duration-200 ease-mogged hover:border-aurum-bright hover:shadow-aurum-glow text-[10px] lg:text-xs"
        >
          <LuPlus size={14} aria-hidden="true" />
          <span className="hidden lg:inline">NEW GLOWUP</span>
          <span className="lg:hidden">NEW</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-6 mt-3.5 lg:mt-5">
        {rows.map((row) => (
          <GlowupCard key={row.glowup_id} row={row} onView={onView} />
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-9">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            className="font-display uppercase tracking-[0.22em] text-xs text-bone-dim border border-hairline-soft rounded-field px-7 py-3 cursor-pointer transition-colors duration-200 hover:text-bone hover:border-[rgba(255,255,255,0.3)] disabled:opacity-50 disabled:cursor-default"
          >
            {loadingMore ? 'LOADING…' : 'LOAD MORE'}
          </button>
        </div>
      )}
    </div>
  );
};

const GlowupCard = ({ row, onView }) => (
  <article className="bg-[#1B1B1A] border border-hairline-soft rounded-card p-3.5 lg:p-[22px]">
    <div className="flex justify-between items-center gap-3">
      <div className="min-w-0">
        <p
          className="font-display uppercase tracking-[0.22em] text-bone-dim m-0"
          style={{ fontSize: 11 }}
        >
          {formatSpanLabel(row.before_taken_at, row.after_taken_at)}
        </p>
        <p className="font-display text-bone m-0 mt-1 text-xl lg:text-2xl">
          {glowupTitle(row)}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        {row.biggest_vector && (
          <span className="flex items-center gap-2 border border-[rgba(201,164,92,0.4)] rounded-full px-3 py-[7px] lg:px-3.5 lg:py-2">
            <LuTrendingUp size={14} className="text-aurum" aria-hidden="true" />
            <span
              className="font-display uppercase tracking-[0.14em] text-aurum"
              style={{ fontSize: 12 }}
            >
              {glowupVectorLabel(row.biggest_vector)}
            </span>
          </span>
        )}
        {row.responses_seen != null && (
          <span
            className="font-display uppercase tracking-[0.18em] text-bone-dim"
            style={{ fontSize: 9.5 }}
          >
            {row.responses_seen} JUDGMENTS
          </span>
        )}
      </div>
    </div>

    <div className="grid grid-cols-2 gap-2.5 lg:gap-3.5 mt-3.5 lg:mt-[18px]">
      <GlowupSide
        label={COPY.GLOWUP_SIDE_BEFORE_LABEL}
        imageUrl={row.before_thumbnail_url}
      />
      <GlowupSide
        label={COPY.GLOWUP_SIDE_AFTER_LABEL}
        imageUrl={row.after_thumbnail_url}
      />
    </div>

    <div className="flex justify-between items-center mt-3 lg:mt-4 pt-3 lg:pt-3.5 border-t border-hairline-soft">
      <span className="font-display text-bone-dim" style={{ fontSize: 13 }}>
        {formatMonthYear(row.before_taken_at)} &rarr; {formatMonthYear(row.after_taken_at)}
      </span>
      <button
        type="button"
        onClick={() => onView && onView(row.glowup_id)}
        className="flex items-center gap-2 bg-transparent border-0 cursor-pointer text-[#D8D2C6] hover:text-bone font-display uppercase tracking-[0.2em] p-0"
        style={{ fontSize: 11 }}
      >
        <span>VIEW</span>
        <LuArrowRight size={14} aria-hidden="true" />
      </button>
    </div>
  </article>
);

// One before/after column. No scores exist in OwnGlowupRow, so the eye-line
// censor bar is the ANONYMOUS treatment — solid black at the canonical
// EYE_LINE_PCT eye-line, no numeral (same geometry as PortraitScoreBar, empty).
const GlowupSide = ({ label, imageUrl }) => (
  <div>
    <p
      className="font-display uppercase tracking-[0.2em] text-bone-dim text-center m-0 mb-2"
      style={{ fontSize: 9.5 }}
    >
      {label}
    </p>
    <div className="relative aspect-[3/4] rounded-thumb overflow-hidden border border-hairline-soft bg-vein">
      {imageUrl ? (
        <img src={imageUrl} alt={`${label.toLowerCase()} portrait`} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-vein" role="img" aria-label={`${label.toLowerCase()} portrait placeholder`} />
      )}
      <div
        role="img"
        aria-label="anonymous portrait"
        className="absolute left-[8%] right-[8%] bg-obsidian"
        style={{ top: `calc(${EYE_LINE_PCT}% - 20px)`, height: 40 }}
      />
    </div>
  </div>
);

export default VaultGlowupsView;
